import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Package,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  Layers,
} from "lucide-react";
import { GlassPanel, formatCrmCurrency, formatNumber, ThemedLegend, getAxisProps, getGridProps, getAngledAxisProps, getTooltipComponent, CATEGORY_COLORS, CATEGORY_GRADIENTS, IncompleteState } from "./ReportShared";
import { useCrmLocale } from "../../SalonCRM/i18n/CrmLocale";
import { DateRange, filterMonthly } from "../analyticsDateRange";
import type { LiveAnalytics } from "../liveAnalyticsAdapter";

const STOCK_STYLES_DARK: Record<string, { bg: string; text: string }> = {
  high:     { bg: "bg-emerald-500/15",  text: "text-emerald-400" },
  medium:   { bg: "bg-blue-500/15",     text: "text-blue-400" },
  low:      { bg: "bg-amber-500/15",    text: "text-amber-400" },
  critical: { bg: "bg-rose-500/15",     text: "text-rose-400" },
};

const STOCK_STYLES_LIGHT: Record<string, { bg: string; text: string }> = {
  high:     { bg: "bg-emerald-100",  text: "text-emerald-700" },
  medium:   { bg: "bg-blue-100",     text: "text-blue-700" },
  low:      { bg: "bg-amber-100",    text: "text-amber-700" },
  critical: { bg: "bg-rose-100",     text: "text-rose-700" },
};

const ProductUsageReport: React.FC<{ dateRange: DateRange; isDark: boolean; analytics: LiveAnalytics }> = ({ dateRange, isDark, analytics }) => {
  const { lang, t } = useCrmLocale();
  const r = t.analytics.report;
  const categoryLabel = (category: string) => {
    const displayKey = category === "Cut" || category === "Other" ? "Others" : category;
    return t.analytics.categories[displayKey as keyof typeof t.analytics.categories] ?? category;
  };
  const fc = (v: number) => formatCrmCurrency(v, lang);
  const PRODUCTS = analytics.products;
  const MONTHLY_PRODUCTS = analytics.monthlyProducts;

  const f = useMemo(() => {
    const months = filterMonthly(MONTHLY_PRODUCTS, dateRange);

    const totalUsage = months.reduce((s, m) => s + m.totalUsage, 0);
    const totalCost = months.reduce((s, m) => s + m.totalCost, 0);

    // Grams + cost are both attributed to the *visit service* category in the
    // adapter (not the product SKU type). Cost fields are explicit so we never
    // re-allocate expensive color grams vs cheap developer grams by weight.
    const catKeys = ["Color", "Highlights", "Toner", "Straightening", "Treatment", "Others"] as const;
    const costKey = {
      Color: "ColorCost",
      Highlights: "HighlightsCost",
      Toner: "TonerCost",
      Straightening: "StraighteningCost",
      Treatment: "TreatmentCost",
      Others: "OthersCost",
    } as const;
    const catData = catKeys.map(name => {
      const usage = months.reduce((s, m) => s + (m[name] || 0), 0);
      const cost = months.reduce((s, m) => s + (m[costKey[name]] || 0), 0);
      const products = PRODUCTS.filter(p => p.category === name);
      return { name, totalUsage: usage, totalCost: Math.round(cost), productCount: products.length };
    }).filter(c => c.totalUsage > 0 || c.totalCost > 0).sort((a, b) => b.totalCost - a.totalCost);

    return { months, totalUsage, totalCost, catData };
  }, [dateRange, PRODUCTS, MONTHLY_PRODUCTS]);

  const pieData = f.catData.map(c => ({ name: categoryLabel(c.name), key: c.name, value: c.totalUsage }));
  const costByCategory = f.catData.map(c => ({ name: categoryLabel(c.name), key: c.name, cost: c.totalCost, color: CATEGORY_COLORS[c.name] || "#64748B" }));

  const sortedProducts = [...PRODUCTS].sort((a, b) => b.cost - a.cost || b.usageGrams - a.usageGrams);
  const lowStockProducts = PRODUCTS.filter(p => p.stockLevel === "low" || p.stockLevel === "critical");

  const STOCK_STYLES = isDark ? STOCK_STYLES_DARK : STOCK_STYLES_LIGHT;

  const txt = isDark ? "text-white" : "text-[#1A1A1A]";
  const txtMuted = isDark ? "text-gray-500" : "text-gray-500";
  const txtMid = isDark ? "text-gray-400" : "text-gray-500";
  const borderSep = isDark ? "border-white/[0.06]" : "border-black/[0.06]";
  const stripeBg = isDark ? "bg-white/[0.02]" : "bg-black/[0.02]";
  const stripeHover = isDark ? "hover:bg-white/[0.06]" : "hover:bg-black/[0.04]";
  const stripeBorder = isDark ? "border-white/[0.04]" : "border-black/[0.04]";

  const TooltipComp = getTooltipComponent(isDark);
  const axisProps = getAxisProps(isDark);
  const gridProps = getGridProps(isDark);
  const angledAxisProps = getAngledAxisProps(isDark);

  const pieTooltipStyle = isDark
    ? { background: "rgba(10,10,14,0.88)", backdropFilter: "blur(16px)", boxShadow: "0 8px 32px rgba(0,0,0,0.50)" }
    : { background: "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", boxShadow: "0 8px 32px rgba(0,0,0,0.10)" };
  const pieTooltipBorder = isDark ? "border-white/[0.08]" : "border-black/[0.06]";
  const pieTooltipName = isDark ? "text-white" : "text-gray-900";
  const pieTooltipVal = isDark ? "text-gray-400" : "text-gray-500";

  if (PRODUCTS.length === 0) {
    return (
      <IncompleteState
        isDark={isDark}
        title={r.noProductsTitle}
        description={r.noProductsDescription}
      />
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ── KPI Cards ───────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {([
          { icon: DollarSign, label: r.totalProductCost, value: fc(f.totalCost), gradient: "from-amber-500 to-orange-600", subtitle: r.directMaterialRecorded },
          { icon: Package, label: r.totalUsage, value: `${formatNumber(f.totalUsage, lang)}g`, gradient: "from-teal-500 to-emerald-600", subtitle: `${PRODUCTS.length} ${r.products}` },
          { icon: Layers, label: r.categories, value: String(f.catData.length), gradient: "from-violet-500 to-purple-600", subtitle: r.activeCategories },
          { icon: AlertTriangle, label: r.lowStockAlerts, value: String(lowStockProducts.length), gradient: "from-rose-500 to-pink-600", subtitle: r.needsAttention },
        ] as const).map(({ icon: Icon, label, value, gradient, subtitle }) => (
          <GlassPanel key={label} variant="chartDark" isDark={isDark} className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`} style={{ boxShadow: "0 0 16px rgba(0,0,0,0.3)" }}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className={`text-[11px] ${txtMuted} font-medium`}>{label}</p>
                <p className={`text-lg sm:text-xl font-bold ${txt} tracking-tight`}>{value}</p>
                <p className={`text-[10px] ${txtMuted} mt-0.5`}>{subtitle}</p>
              </div>
            </div>
          </GlassPanel>
        ))}
      </div>

      {/* ── Category Breakdown + Cost Analysis ──────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Category Pie */}
        <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
          <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex items-center gap-2.5`}>
            <Layers className="w-4 h-4 text-teal-400" style={{ filter: "drop-shadow(0 0 6px rgba(20,184,166,0.5))" }} />
            <h3 className={`text-[13px] font-bold ${txt}`}>{r.usageByCategory}</h3>
            <span className={`text-[10px] ${txtMuted} ms-1`}>{r.consumptionBreakdown}</span>
          </div>
          <div className="p-4 sm:p-6">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={4}
                  dataKey="value"
                  stroke="none"
                  cornerRadius={5}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={CATEGORY_COLORS[entry.key] || "#64748B"} fillOpacity={0.9} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0];
                    return (
                      <div className={`rounded-xl p-3 text-sm border ${pieTooltipBorder}`} style={pieTooltipStyle}>
                        <p className={`font-semibold ${pieTooltipName}`}>{d.name}</p>
                        <p className={pieTooltipVal}>{formatNumber(d.value, lang)}g</p>
                      </div>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3">
              <ThemedLegend isDark={isDark} items={pieData.map(e => ({ label: e.name, color: CATEGORY_COLORS[e.key] || "#64748B" }))} />
            </div>
          </div>
        </GlassPanel>

        {/* Cost by Category */}
        <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
          <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex items-center gap-2.5`}>
            <DollarSign className="w-4 h-4 text-amber-400" style={{ filter: "drop-shadow(0 0 6px rgba(245,158,11,0.5))" }} />
            <h3 className={`text-[13px] font-bold ${txt}`}>{r.costByCategory}</h3>
          </div>
          <div className="p-4 sm:p-6">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={costByCategory}>
                <defs>
                  {costByCategory.map((entry) => {
                    const grad = CATEGORY_GRADIENTS[entry.key];
                    const c0 = grad ? grad[0] : entry.color;
                    const c1 = grad ? grad[1] : entry.color;
                    return (
                      <linearGradient key={`prodCostBar-${entry.key}`} id={`prodCostBar-${entry.key}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={c0} stopOpacity={0.90} />
                        <stop offset="100%" stopColor={c1} stopOpacity={0.25} />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="name" {...axisProps} />
                <YAxis {...axisProps} />
                <Tooltip content={<TooltipComp />} />
                <Bar dataKey="cost" name={r.cost} radius={[8, 8, 2, 2]}>
                  {costByCategory.map((entry) => (
                    <Cell key={entry.key} fill={`url(#prodCostBar-${entry.key})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassPanel>
      </div>

      {/* ── Monthly Usage Trend ─────────────────────────── */}
      <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
        <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex items-center justify-between`}>
          <div className="flex items-center gap-2.5">
            <TrendingUp className="w-4 h-4 text-teal-400" style={{ filter: "drop-shadow(0 0 6px rgba(20,184,166,0.5))" }} />
            <h3 className={`text-[13px] font-bold ${txt}`}>{r.monthlyUsageTrend}</h3>
            <span className={`text-[10px] ${txtMuted} hidden sm:inline`}>{r.totalConsumption} (g)</span>
          </div>
          <ThemedLegend isDark={isDark} items={[{ label: `${r.usage} (g)`, color: "#14B8A6" }]} />
        </div>
        <div className="p-4 sm:p-6">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={f.months}>
              <defs>
                <linearGradient id="usageTrendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#14B8A6" stopOpacity={0.45} />
                  <stop offset="50%" stopColor="#14B8A6" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#14B8A6" stopOpacity={0} />
                </linearGradient>
                <filter id="usageGlow">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="month" {...angledAxisProps} height={44} />
              <YAxis {...axisProps} />
              <Tooltip content={<TooltipComp />} />
              <Area
                type="monotone"
                dataKey="totalUsage"
                name={`${r.usage} (g)`}
                stroke="#14B8A6"
                strokeWidth={2.5}
                fill="url(#usageTrendGrad)"
                dot={{ r: 3.5, fill: "#14B8A6", stroke: "rgba(20,184,166,0.3)", strokeWidth: 4 }}
                filter="url(#usageGlow)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassPanel>

      {/* ── Product Inventory Table ─────────────────────── */}
      <GlassPanel variant="chartDark" isDark={isDark} className="overflow-hidden">
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2 sm:pb-3">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-teal-400" style={{ filter: "drop-shadow(0 0 6px rgba(20,184,166,0.5))" }} />
            <h3 className={`text-sm font-bold ${txt}`}>{r.productInventory}</h3>
          </div>
          <p className={`text-[11px] ${txtMuted}`}>{r.productInventoryDescription}</p>
        </div>
        <div className="px-4 sm:px-6 pb-4 sm:pb-5">
          <div className={`rounded-2xl overflow-x-auto border ${borderSep}`}>
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className={isDark ? "bg-white/[0.03]" : "bg-black/[0.02]"}>
                  <th className={`text-start px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.product}</th>
                  <th className={`text-start px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.brand}</th>
                  <th className={`text-start px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.category}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.cost}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.costPerGram}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.usage} (g)</th>
                  <th className={`text-center px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.stock}</th>
                  <th className={`text-end px-4 py-3 font-semibold ${txtMuted} text-[11px] uppercase tracking-wider`}>{r.trend}</th>
                </tr>
              </thead>
              <tbody>
                {sortedProducts.map((p, i) => {
                  const stock = STOCK_STYLES[p.stockLevel];
                  return (
                    <tr
                      key={p.id}
                      className={`border-t ${stripeBorder} ${stripeHover} transition-colors ${
                        i % 2 === 0 ? stripeBg : ""
                      }`}
                    >
                      <td className={`px-4 py-3.5 font-semibold ${txt}`}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-1.5 h-6 rounded-full"
                            style={{ backgroundColor: CATEGORY_COLORS[p.category] || "#64748B" }}
                          />
                          {p.name}
                        </div>
                      </td>
                      <td className={`px-4 py-3.5 ${txtMid}`}>{p.brand}</td>
                      <td className={`px-4 py-3.5 ${txtMid}`}>{categoryLabel(p.category)}</td>
                      <td className={`text-end px-4 py-3.5 font-bold ${txt}`}>{fc(p.cost)}</td>
                      <td className={`text-end px-4 py-3.5 ${txtMid}`}>
                        {p.unitPrice > 0 ? fc(p.unitPrice) : "—"}
                      </td>
                      <td className={`text-end px-4 py-3.5 ${txtMid}`}>{formatNumber(p.usageGrams, lang)}</td>
                      <td className="text-center px-4 py-3.5">
                        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${stock.bg} ${stock.text}`}>
                          {t.analytics.stock[p.stockLevel]}
                        </span>
                      </td>
                      <td className="text-end px-4 py-3.5">
                        <span className={`text-[12px] font-semibold ${p.trend >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          {p.trend >= 0 ? "+" : ""}{p.trend}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
};

export default ProductUsageReport;
